import React from 'react';

export interface TableProps {
  children: React.ReactNode;
}

// Simple markdown table parser
const parseMarkdownTable = (markdown: string): { headers: string[], rows: string[][] } => {
  const lines = markdown.trim().split('\n').map(line => line.trim()).filter(line => line);
  
  if (lines.length < 3) {
    throw new Error('Invalid table format. Need at least header, separator, and one data row.');
  }
  
  // Parse header row
  const headerLine = lines[0];
  const headers = headerLine.split('|').map(cell => cell.trim()).filter(cell => cell);
  
  // Skip separator line (line[1])
  
  // Parse data rows
  const rows: string[][] = [];
  for (let i = 2; i < lines.length; i++) {
    const row = lines[i].split('|').map(cell => cell.trim()).filter(cell => cell);
    if (row.length > 0) {
      rows.push(row);
    }
  }
  
  return { headers, rows };
};

// Convert markdown formatting to JSX
const formatCellContent = (content: string): React.ReactNode => {
  // Handle **bold** text
  const parts = content.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Helper function to extract text content from React elements
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string') {
    return children;
  }
  
  if (React.isValidElement(children)) {
    // If it's a React element, extract text from its children
    return extractTextFromChildren(children.props.children);
  }
  
  if (Array.isArray(children)) {
    // If it's an array of children, join them
    return children.map(child => extractTextFromChildren(child)).join('');
  }
  
  // For other types (numbers, etc.), convert to string
  return String(children || '');
};

export const Table: React.FC<TableProps> = ({ children }) => {
  try {
    // Extract text content from React elements
    const markdownString = extractTextFromChildren(children);
    const { headers, rows } = parseMarkdownTable(markdownString);
    
    return (
      <table>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index}>{formatCellContent(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{formatCellContent(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  } catch (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Table Error:</strong> {error instanceof Error ? error.message : 'Invalid table format'}
      </div>
    );
  }
};

export default Table;